import { useEffect, useState } from "react";

// Maximum processing time in seconds
const MAX_PROCESSING_SECONDS = 30;

// Processing messages to display sequentially based on progress
const PROCESSING_MESSAGES = [
  "Transcribing your audio...", // show first (0-25%)
  "Extracting key details...", // show when progress is above 25%
  "Validating exercise & weight...", // show when progress is above 50%
  "Finishing up...", // show when progress is above 75%
];

// Progress thresholds for showing each message
const MESSAGE_THRESHOLDS = [0, 25, 50, 75];

// Countdown time for auto-refresh (in seconds)
const REFRESH_COUNTDOWN_SECONDS = 5;

// Sound wave animation styles
const audioWaveStyles = `
.sound-wave-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  width: 220px;
  gap: 6px;
}

.sound-wave-bar {
  background-color: var(--color-accent-fg-hot);
  width: 3px;
  height: 100%;
  border-radius: 2px;
  transform-origin: bottom;
  animation: sound-wave-animation 2s ease-in-out infinite;
  opacity: 0.85;
}

.sound-wave-bar:nth-child(1) { 
  height: 18%; 
  animation-delay: 0.1s; 
  width: 2px;
}
.sound-wave-bar:nth-child(2) { 
  height: 37%; 
  animation-delay: 0.5s; 
  width: 2.5px;
}
.sound-wave-bar:nth-child(3) { 
  height: 28%; 
  animation-delay: 0.3s; 
}
.sound-wave-bar:nth-child(4) { 
  height: 62%; 
  animation-delay: 0.7s; 
  width: 3.5px;
}
.sound-wave-bar:nth-child(5) { 
  height: 43%; 
  animation-delay: 0.2s; 
  width: 2px;
}
.sound-wave-bar:nth-child(6) { 
  height: 70%; 
  animation-delay: 0.9s; 
  width: 4px;
}
.sound-wave-bar:nth-child(7) { 
  height: 52%; 
  animation-delay: 0.4s; 
  width: 3px;
}
.sound-wave-bar:nth-child(8) { 
  height: 65%; 
  animation-delay: 0.8s; 
  width: 2.5px;
}
.sound-wave-bar:nth-child(9) { 
  height: 33%; 
  animation-delay: 0.6s; 
  width: 2px;
}
.sound-wave-bar:nth-child(10) { 
  height: 48%; 
  animation-delay: 0.2s; 
  width: 3.5px;
}
.sound-wave-bar:nth-child(11) { 
  height: 22%; 
  animation-delay: 0.3s; 
  width: 2px;
}

@keyframes sound-wave-animation {
  0% { transform: scaleY(0.8) scaleX(1); }
  50% { transform: scaleY(1.1) scaleX(1.5); }
  100% { transform: scaleY(0.8) scaleX(1); }
}

.progress-bar-container {
  width: 240px;
  height: 10px;
  padding:2px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 10px;
  overflow: hidden;
  margin: 8px 0;
}

.progress-bar {
  height: 6px;
  background-color: var(--color-accent-bg-hot);
  border-radius: 3px;
  transition: width 0.5s ease;
}

.processing-message {
  min-height: 24px;
  text-align: center;
  transition: opacity 0.5s ease;
}

.error-container {
  padding: 12px;
  border-radius: 8px;
}
`;

interface AudioProcessingModalProps {
  title?: string;
}

/**
 * A modal component that displays an audio wave animation
 * Used to indicate audio processing is in progress
 */
export function AudioProcessingModal({
  title = "Processing Audio",
}: AudioProcessingModalProps) {
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(PROCESSING_MESSAGES[0]);
  const [messageOpacity, setMessageOpacity] = useState(1);
  const [countdownSeconds, setCountdownSeconds] = useState(
    REFRESH_COUNTDOWN_SECONDS
  );

  // Function to refresh the page
  const refreshPage = () => {
    window.location.reload();
  };

  // Handle countdown and auto-refresh when error occurs
  useEffect(() => {
    if (!error) return;

    // Start countdown
    const countdownTimer = setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          refreshPage();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup
    return () => clearInterval(countdownTimer);
  }, [error]);

  // Update message based on progress
  useEffect(() => {
    // Find the appropriate message index based on progress
    let newMessageIndex = 0;
    for (let i = MESSAGE_THRESHOLDS.length - 1; i >= 0; i--) {
      if (progress >= MESSAGE_THRESHOLDS[i]) {
        newMessageIndex = i;
        break;
      }
    }

    // Only update if the message index has changed
    if (newMessageIndex !== currentMessageIndex) {
      // Fade out current message
      setMessageOpacity(0);

      // After fade out, change to new message and fade in
      setTimeout(() => {
        setCurrentMessageIndex(newMessageIndex);
        setCurrentMessage(PROCESSING_MESSAGES[newMessageIndex]);
        setMessageOpacity(1);
      }, 500); // Half a second for fade out
    }
  }, [progress, currentMessageIndex]);

  // Handle progress and timeout
  useEffect(() => {
    const startTime = Date.now();
    let progressTimer: NodeJS.Timeout;

    // Check for timeout every second
    const timer = setInterval(() => {
      const timeElapsed = Date.now() - startTime;
      if (timeElapsed >= MAX_PROCESSING_SECONDS * 1000) {
        setError(true);
        clearTimeout(progressTimer);
      }
    }, 1000);

    // Update progress randomly every 0.5-1.5 seconds
    const updateProgress = () => {
      if (error) return;

      setProgress((prev) => {
        // Calculate a random increment between 2-8%
        const increment = Math.random() * 6 + 2;
        // Ensure we don't exceed 99% (save 100% for actual completion)
        const newProgress = Math.min(prev + increment, 93);
        return newProgress;
      });

      // Schedule next update with random timing
      const nextUpdateTime = Math.random() * 1000 + 500; // 500-1500ms
      progressTimer = setTimeout(updateProgress, nextUpdateTime);
    };

    // Start progress updates
    updateProgress();

    // Cleanup
    return () => {
      clearInterval(timer);
      clearTimeout(progressTimer);
    };
  }, [error]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{
        backdropFilter: "blur(4px)",
        backgroundColor: "rgba(13, 28, 24, 0.85)",
        zIndex: 9999, // Use a high z-index value to ensure it appears above the BottomNavbar
      }}
    >
      <div className="flex flex-col  justify-center items-center p-8 rounded-xl bg-white/10 text-white max-w-xs">
        {!error && (
          <div>
            <div className="sound-wave-container mb-4">
              <div className="sound-wave-bar"></div>
              <div className="sound-wave-bar"></div>
              <div className="sound-wave-bar"></div>
              <div className="sound-wave-bar"></div>
              <div className="sound-wave-bar"></div>
              <div className="sound-wave-bar"></div>
              <div className="sound-wave-bar"></div>
              <div className="sound-wave-bar"></div>
              <div className="sound-wave-bar"></div>
              <div className="sound-wave-bar"></div>
              <div className="sound-wave-bar"></div>
            </div>
            <div className="flex justify-center">
              <h3 className="text-base font-bold mb-2">{title}</h3>
            </div>
          </div>
        )}
        {error ? (
          <div className="error-container" onClick={refreshPage}>
            <p className="text-center text-white">
              Oops, our server hit a brief glitch. Will refresh in{" "}
              {countdownSeconds} seconds - please try again.
            </p>
          </div>
        ) : (
          <>
            <div className="progress-bar-container">
              <div
                className="progress-bar"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p
              className="text-xs text-gray-300 mt-1"
              style={{ opacity: messageOpacity }}
            >
              {currentMessage}
            </p>

            {/* <p className="text-xs text-gray-300 mt-1">
              {Math.round(progress)}%
            </p> */}
          </>
        )}

        <style>{audioWaveStyles}</style>
      </div>
    </div>
  );
}
